/**
 * VIDEO CAPTURE COMPONENT - NATIVE CAMERA
 *
 * Uses native camera app for best mobile UX:
 * - Triggers native video recording on mobile devices
 * - Full-screen video recording experience users expect
 * - All native camera features (flash, zoom, focus)
 * - Falls back to file picker on desktop
 * - Shows preview after capture with retake option
 */

import { useRef, useState, useEffect } from 'react'
import * as Sentry from '@sentry/react'

type Props = {
  submissionId: string
  writeToken?: string | null
  side: 'left' | 'right'
  position?: string
  maxSeconds?: number
  existingMedia?: boolean | string
  onSaved: (uid: string) => void
}

type AssetMap = Record<string, { cf_stream_uid?: string }>
type AssetsResponse = { assets?: AssetMap }
type UploadResponse = { cloudflareId?: string; error?: string }

function isSyntheticUid(uid: string) {
  return uid.startsWith('fallback-') || uid.startsWith('dev-video-')
}

function isLikelyStreamUid(uid: string) {
  return /^[a-z0-9]{32}$/i.test(uid)
}

export default function VideoCapture({ submissionId, writeToken, side, position, existingMedia, onSaved }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const previewRef = useRef<HTMLDivElement>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [videoPlayError, setVideoPlayError] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [showStreamPreview, setShowStreamPreview] = useState(false)

  // Track if user has manually retaken (to prevent auto-restore after retake)
  const [hasRetaken, setHasRetaken] = useState(false)

  // Track if we just captured a video (to prevent switching to Stream ID preview)
  const [justCaptured, setJustCaptured] = useState(false)

  // Scroll preview into center view after capture
  const scrollToPreview = () => {
    setTimeout(() => {
      previewRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
    }, 100)
  }

  // Clean up object URLs on unmount
  useEffect(() => {
    return () => {
      if (preview && preview.startsWith('blob:')) {
        URL.revokeObjectURL(preview)
      }
    }
  }, [preview])

  // Handle existing media on component mount (but not after retake or fresh capture)
  useEffect(() => {
    const loadExistingMedia = async () => {
      if (existingMedia && !preview && !hasRetaken && !justCaptured) {
        // Check if existingMedia is already a Stream ID
        if (typeof existingMedia === 'string' && existingMedia !== 'true') {
          setPreview(existingMedia)
          return
        }

        // Otherwise, fetch video info from database
        try {
          // Skip fetch on localhost (no backend in dev)
          if (window.location.hostname === 'localhost') {
            setPreview('existing-video-placeholder')
            return
          }

          const headers: Record<string, string> = {}
          if (writeToken) {
            headers['Authorization'] = `Bearer ${writeToken}`
          }
          const response = await fetch(`/api/assets/${submissionId}`, { headers })
          if (response.ok) {
            const data = await response.json() as AssetsResponse
            const mediaKey = `video_${side}_${position}`
            const asset = data.assets?.[mediaKey]

            if (asset && asset.cf_stream_uid) {
              setPreview(asset.cf_stream_uid)
              return
            }
          }
        } catch (error) {
          Sentry.captureException(error, { tags: { action: 'fetch_video_info', side, position } })
        }

        // Fallback to placeholder
        setPreview('existing-video-placeholder')
      }
    }

    loadExistingMedia()
  }, [existingMedia, submissionId, side, position, preview, hasRetaken, justCaptured])

  // Handle video capture from native camera
  const handleVideoCapture = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0]
    if (!selectedFile) return

    // Create preview URL
    const previewUrl = URL.createObjectURL(selectedFile)
    setPreview(previewUrl)
    setShowStreamPreview(false)
    setFile(selectedFile)
    setJustCaptured(true)
    setVideoPlayError(false)
    scrollToPreview()

    // Auto-save video immediately after capture
    await handleSaveVideo(selectedFile)
  }

  // Trigger native video recording
  const recordVideo = () => {
    fileInputRef.current?.click()
  }

  // Reset to record another video
  const retakeVideo = () => {
    if (preview && preview.startsWith('blob:')) {
      URL.revokeObjectURL(preview)
    }
    setPreview(null)
    setFile(null)
    setHasRetaken(true)
    setJustCaptured(false)
    setVideoPlayError(false)
    setShowStreamPreview(false)
    setUploadError(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // Save video to database and call onSaved callback
  const handleSaveVideo = async (videoFile?: File) => {
    const fileToSave = videoFile || file
    if (!fileToSave) return

    setUploading(true)
    setUploadError(null)

    try {
      // Development mode - simulate success
      if (window.location.hostname === 'localhost') {
        await new Promise(resolve => setTimeout(resolve, 1500))
        const fakeUid = `dev-video-${side}-${Date.now()}`
        onSaved(fakeUid)
        return
      }

      // Production mode - actually upload
      const formData = new FormData()
      formData.append('file', fileToSave)
      formData.append('submissionId', submissionId)
      formData.append('side', side)
      formData.append('position', position || '')

      const headers: Record<string, string> = {}
      if (writeToken) {
        headers['Authorization'] = `Bearer ${writeToken}`
      }

      const response = await fetch('/api/uploads/video', {
        method: 'POST',
        headers,
        body: formData
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({} as UploadResponse)) as UploadResponse
        throw new Error(errorData.error || `Upload failed: ${response.status}`)
      }

      const result = await response.json() as UploadResponse
      if (!result.cloudflareId) {
        throw new Error('Upload response missing cloudflareId')
      }
      onSaved(result.cloudflareId)

    } catch (error) {
      Sentry.captureException(error, { tags: { action: 'video_upload', side, position } })
      setUploadError('Failed to save video. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="space-y-3">
      {/* Hidden file input that triggers native video recording */}
      <input
        ref={fileInputRef}
        type="file"
        accept="video/*"
        capture="environment"
        onChange={handleVideoCapture}
        className="hidden"
      />

      {!preview ? (
        // Record State: Show button to trigger native video recording
        <div className="space-y-3">
          <button
            onClick={recordVideo}
            disabled={uploading}
            className="w-full bg-blue-500 text-white py-4 px-6 rounded-lg font-medium text-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? 'Processing...' : `Record ${side} hand video`}
          </button>
        </div>
      ) : (
        // Preview State: Show captured video with options
        <div className="space-y-3">
          {/* Video Preview */}
          <div ref={previewRef} className="relative">
            {preview === 'existing-video-placeholder' ? (
              <div className="w-full bg-blue-50 rounded-lg border-2 border-blue-200 flex flex-col items-center justify-center text-blue-700 p-6" style={{ aspectRatio: '3/4', maxHeight: '40vh' }}>
                <div className="text-4xl mb-2">&#x1f3a5;</div>
                <div className="text-center">
                  <div className="font-medium">Video Recorded</div>
                  <div className="text-sm mt-1">Previously saved to database</div>
                  <div className="text-xs text-blue-600 mt-1">Click Retake to replace</div>
                </div>
              </div>
            ) : preview && !preview.startsWith('blob:') && preview !== 'existing-video-placeholder' ? (
              isSyntheticUid(preview) || !isLikelyStreamUid(preview) ? (
                <div className="w-full bg-blue-50 rounded-lg border-2 border-blue-200 flex flex-col items-center justify-center text-blue-700 p-6" style={{ aspectRatio: '3/4', maxHeight: '40vh' }}>
                  <div className="text-4xl mb-2">&#x1f3a5;</div>
                  <div className="text-center">
                    <div className="font-medium">Video saved</div>
                    <div className="text-sm mt-1">Preview is unavailable for this recording.</div>
                    <div className="text-xs text-blue-600 mt-1">You can continue or retake this step.</div>
                  </div>
                </div>
              ) : (
                <div className="w-full space-y-2">
                  {!showStreamPreview ? (
                    <div className="w-full bg-blue-50 rounded-lg border-2 border-blue-200 flex flex-col items-center justify-center text-blue-700 p-6" style={{ aspectRatio: '3/4', maxHeight: '40vh' }}>
                      <div className="text-4xl mb-2">&#x2705;</div>
                      <div className="text-center">
                        <div className="font-medium">Video uploaded</div>
                        <div className="text-sm mt-1">Cloudflare may still be processing this video.</div>
                        <div className="text-xs text-blue-600 mt-1">You can continue now, or tap below to try preview.</div>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full rounded-lg border-2 border-gray-200 overflow-hidden relative" style={{ aspectRatio: '3/4', maxHeight: '40vh' }}>
                      <iframe
                        src={`https://iframe.videodelivery.net/${preview}?muted=true&autoplay=false&controls=true&loop=true&letterboxColor=transparent`}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title={`${side} hand video ${position}`}
                      />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowStreamPreview(prev => !prev)}
                    className="w-full text-sm bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-700 hover:bg-gray-50"
                  >
                    {showStreamPreview ? 'Hide Preview' : 'Try Preview'}
                  </button>
                </div>
              )
            ) : preview && preview.startsWith('blob:') ? (
              // Actual video preview from file
              <div className="w-full rounded-lg border-2 border-gray-200 overflow-hidden" style={{ aspectRatio: '3/4', maxHeight: '40vh' }}>
                {videoPlayError ? (
                  <div className="w-full h-full bg-blue-50 flex flex-col items-center justify-center text-blue-700 p-6">
                    <div className="text-3xl mb-2">&#x1f4f9;</div>
                    <div className="text-center">
                      <div className="font-medium">Video captured but preview unavailable on this device.</div>
                      <div className="text-sm mt-1">Your video was saved successfully.</div>
                      <div className="text-xs text-blue-600 mt-1">Tap Retake to re-record if needed.</div>
                    </div>
                  </div>
                ) : (
                  <video
                    src={preview}
                    autoPlay
                    muted
                    loop
                    playsInline
                    controls
                    onError={() => setVideoPlayError(true)}
                    className="w-full h-full object-contain"
                  />
                )}
              </div>
            ) : null
            }
            {uploading && (
              <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center">
                <div className="bg-white px-4 py-2 rounded-lg flex items-center space-x-2">
                  <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                  <span className="text-sm font-medium">Saving video...</span>
                </div>
              </div>
            )}
          </div>

          {/* Upload error message */}
          {uploadError && (
            <div className="text-red-500 text-sm text-center p-2 bg-red-50 rounded-lg">
              {uploadError}
            </div>
          )}

          {/* Action Button */}
          <div className="flex justify-center">
            <button
              onClick={retakeVideo}
              disabled={uploading}
              className="bg-gray-500 text-white py-3 px-6 rounded-lg font-medium hover:bg-gray-600 transition-colors disabled:opacity-50"
            >
              Retake
            </button>
          </div>

          {/* File Info (Development) */}
          {file && window.location.hostname === 'localhost' && (
            <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
              <strong>File:</strong> {file.name} ({Math.round(file.size / 1024)}KB)
            </div>
          )}
        </div>
      )}
    </div>
  )
}
